import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import Lenis from "lenis";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Loader2,
  Mail,
  ShieldCheck,
  ChevronRight,
} from "lucide-react";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { IdentityVerificationStep } from "@/components/wizard/IdentityVerificationStep";
import { translations } from "@/i18n/translations";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = `${process.env.PUBLIC_URL || ""}/logo-somnium.png`;

// ============================================================
// Global smooth-scroll (Lenis) — subtle premium momentum
// ============================================================
const useLenis = () => {
  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.15,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
    });
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
    };
  }, []);
};

// ============================================================
// Shared UI atoms
// ============================================================
const ScreenShell = ({ children }) => (
  <div className="min-h-[100svh] bg-black text-white relative overflow-x-hidden">
    <div
      className="pointer-events-none fixed inset-0 opacity-[0.08]"
      style={{
        background:
          "radial-gradient(50% 40% at 20% 10%, rgba(212,175,102,0.35) 0%, rgba(0,0,0,0) 60%), radial-gradient(45% 35% at 90% 90%, rgba(212,175,102,0.25) 0%, rgba(0,0,0,0) 60%)",
      }}
    />
    <div className="relative z-10">{children}</div>
  </div>
);

const AppHeader = ({ locale, onLangToggle, refNumber, progress, onExit, exitLabel, langLabel }) => (
  <header className="sticky top-0 z-30 backdrop-blur-md bg-black/70 border-b border-white/5">
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-4 flex items-center justify-between gap-6">
      <a href="/" className="flex items-center gap-3 shrink-0">
        <img src={LOGO_URL} alt="Somnium Group" className="w-7 h-7 object-contain" draggable={false} />
        <span className="font-serif-display text-white/90 text-sm tracking-mid hidden sm:block">
          SOMNIUM GROUP
        </span>
      </a>
      {refNumber ? (
        <div className="hidden md:flex items-center gap-4 text-[10px] tracking-luxe uppercase text-white/40">
          <span className="text-[#e6c98a]/90 font-serif-display tracking-mid text-sm">
            {refNumber}
          </span>
          {typeof progress === "number" && (
            <span className="text-white/50">{progress}% {progress >= 100 ? "" : ""}</span>
          )}
        </div>
      ) : null}
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={onLangToggle}
          className="text-white/60 hover:text-white text-[11px] tracking-luxe uppercase transition-colors"
          data-testid="app-lang-toggle"
        >
          {langLabel}
        </button>
        {onExit && (
          <button
            type="button"
            onClick={onExit}
            className="text-white/40 hover:text-white text-[11px] tracking-luxe uppercase transition-colors"
            data-testid="app-exit"
          >
            {exitLabel}
          </button>
        )}
      </div>
    </div>
    {typeof progress === "number" && (
      <div className="h-[2px] bg-white/5 relative overflow-hidden">
        <motion.div
          className="absolute inset-y-0 left-0 bg-[#e6c98a]"
          initial={{ width: 0 }}
          animate={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        />
      </div>
    )}
  </header>
);

const Chapter = ({ number, label }) => (
  <div className="flex items-center gap-4">
    <span className="font-serif-display text-[#e6c98a] text-2xl leading-none">{number}</span>
    <span className="text-[10px] tracking-luxe uppercase text-white/50">{label}</span>
  </div>
);

// Line-by-line masked reveal for editorial titles
const RevealTitle = ({ children, className = "" }) => {
  const reduce = useReducedMotion();
  const text = typeof children === "string" ? children : "";
  return (
    <h1
      className={`font-serif-display text-white leading-[1.1] ${className}`}
      style={{ letterSpacing: "0.005em" }}
    >
      {reduce ? (
        text
      ) : (
        <span className="block overflow-hidden">
          <motion.span
            className="block"
            initial={{ y: "110%" }}
            animate={{ y: "0%" }}
            transition={{ duration: 1.0, ease: [0.22, 1, 0.36, 1] }}
          >
            {text}
          </motion.span>
        </span>
      )}
    </h1>
  );
};

const FadeInUp = ({ delay = 0, children, className = "" }) => {
  const reduce = useReducedMotion();
  return (
    <motion.div
      initial={reduce ? false : { opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

const Field = ({ id, label, value, onChange, type = "text", placeholder = "", testid, required = false, autoComplete }) => (
  <div className="space-y-2">
    <Label htmlFor={id} className="text-[10px] tracking-luxe uppercase text-white/50">
      {label} {required && <span className="text-[#e6c98a]">*</span>}
    </Label>
    <Input
      id={id}
      type={type}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      autoComplete={autoComplete}
      className="luxe-input h-12 px-4"
      data-testid={testid}
    />
  </div>
);

const luxeButton =
  "inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full text-[11px] tracking-luxe uppercase transition-all duration-300";
const luxeButtonPrimary = `${luxeButton} bg-[#e6c98a] text-black hover:bg-white`;
const luxeButtonGhost = `${luxeButton} border border-white/20 text-white/80 hover:border-white/50 hover:text-white`;

// ============================================================
// Screen 1 — Confirmation (after initial submit)
// ============================================================
const ConfirmationScreen = ({ t, tt, application, locale, onLangToggle, onContinue, onLater }) => (
  <ScreenShell>
    <AppHeader
      locale={locale}
      onLangToggle={onLangToggle}
      langLabel={locale === "fr" ? "EN" : "FR"}
      refNumber={application.reference_number}
    />
    <main className="max-w-3xl mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-32">
      <FadeInUp>
        <Chapter number="00" label={tt.eyebrow} />
      </FadeInUp>
      <div className="mt-8">
        <RevealTitle className="text-4xl sm:text-5xl lg:text-6xl">
          {tt.title}
        </RevealTitle>
      </div>
      <FadeInUp delay={0.35}>
        <p className="mt-8 text-white/70 text-base md:text-lg leading-relaxed max-w-2xl">
          {tt.intro}
        </p>
      </FadeInUp>

      <FadeInUp delay={0.55}>
        <div className="mt-14 border border-white/10 bg-white/[0.02] rounded p-6 md:p-8 max-w-lg">
          <div className="text-[10px] tracking-luxe uppercase text-white/40 mb-2">
            {tt.referenceLabel}
          </div>
          <div className="font-serif-display text-[#e6c98a] text-3xl md:text-4xl tracking-mid">
            {application.reference_number}
          </div>
        </div>
      </FadeInUp>

      <FadeInUp delay={0.75}>
        <div className="mt-16">
          <div className="text-[10px] tracking-luxe uppercase text-white/40 mb-6">
            {tt.stepsTitle}
          </div>
          <ol className="space-y-4">
            {tt.steps.map((s, i) => (
              <li key={i} className="flex items-baseline gap-6 border-b border-white/5 pb-4">
                <span className="font-serif-display text-white/40 text-lg">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="text-white/85 text-base">{s}</span>
              </li>
            ))}
          </ol>
        </div>
      </FadeInUp>

      <FadeInUp delay={0.95}>
        <div className="mt-16 flex flex-col sm:flex-row items-center gap-4">
          <button
            type="button"
            onClick={onContinue}
            className={luxeButtonPrimary}
            data-testid="confirm-continue"
          >
            {tt.primaryCta} <ArrowRight size={14} />
          </button>
          <button
            type="button"
            onClick={onLater}
            className={luxeButtonGhost}
            data-testid="confirm-later"
          >
            <Mail size={14} /> {tt.secondaryCta}
          </button>
        </div>
        <p className="mt-5 text-white/40 text-xs max-w-md">{tt.laterHint}</p>
      </FadeInUp>
    </main>
  </ScreenShell>
);

// ============================================================
// Stepper (progress at top of wizard)
// ============================================================
const STEP_KEYS = ["personal", "selection", "questions", "declarations", "identity", "review"];
const STEP_KEY_TO_NUMBER = { personal: 1, selection: 2, questions: 3, declarations: 4, identity: 5, review: 6 };

const WizardStepper = ({ tt, current }) => {
  const idx = STEP_KEYS.indexOf(current);
  return (
    <div className="hidden md:flex items-center gap-3 text-[10px] tracking-luxe uppercase text-white/40 mb-14">
      {STEP_KEYS.map((k, i) => {
        const done = i < idx;
        const active = i === idx;
        return (
          <div key={k} className="flex items-center gap-3">
            <span
              className={`w-6 h-6 border rounded-full flex items-center justify-center font-serif-display text-[11px] transition-colors ${
                done
                  ? "border-[#e6c98a] text-[#e6c98a]"
                  : active
                  ? "border-white text-white"
                  : "border-white/15 text-white/40"
              }`}
            >
              {done ? <Check size={11} /> : i + 1}
            </span>
            <span className={active ? "text-white" : done ? "text-[#e6c98a]/80" : "text-white/40"}>
              {tt.stages[k]?.label || k}
            </span>
            {i < STEP_KEYS.length - 1 && (
              <ChevronRight size={12} className="text-white/20 mx-1" />
            )}
          </div>
        );
      })}
    </div>
  );
};

// 8 languages of contact — kept in sync with backend/email_templates.py
const CONTACT_LANGUAGES = [
  { value: "fr", label: "Français" },
  { value: "en", label: "English" },
  { value: "es", label: "Español" },
  { value: "de", label: "Deutsch" },
  { value: "it", label: "Italiano" },
  { value: "pt", label: "Português" },
  { value: "ar", label: "العربية" },
  { value: "zh-CN", label: "中文" },
];

// ============================================================
// Step: Personal Information
// ============================================================
const PersonalInfoStep = ({ tt, value, onChange, contactLocale, onContactLocaleChange }) => {
  const s = tt.stages.personal;
  const set = (k) => (v) => onChange({ ...value, [k]: v });
  return (
    <div>
      <Chapter number={s.chapter} label={s.label} />
      <div className="mt-6">
        <RevealTitle className="text-3xl sm:text-4xl lg:text-5xl">{s.title}</RevealTitle>
      </div>
      <FadeInUp delay={0.3}>
        <p className="mt-6 text-white/60 text-base max-w-2xl leading-relaxed">{s.subtitle}</p>
      </FadeInUp>

      {/* Contact language selector — always first, before name */}
      <FadeInUp delay={0.4}>
        <div className="mt-10 max-w-md" data-testid="wizard-contact-locale-wrapper">
          <Label className="text-[10px] tracking-luxe uppercase text-[#e6c98a]/90">
            Contact language / Langue de contact
          </Label>
          <Select value={contactLocale} onValueChange={onContactLocaleChange}>
            <SelectTrigger
              className="luxe-input h-12 px-4 mt-2 border-[#e6c98a]/30"
              data-testid="wizard-contact-locale"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-neutral-950 border-white/10 text-white">
              {CONTACT_LANGUAGES.map((l) => (
                <SelectItem key={l.value} value={l.value} data-testid={`wizard-contact-locale-${l.value}`}>
                  {l.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="mt-2 text-[11px] text-white/40 leading-relaxed">
            Nous vous écrirons dans cette langue tout au long du parcours. Vous pouvez la modifier ici avant de passer à l&apos;étape suivante.
          </p>
        </div>
      </FadeInUp>

      <FadeInUp delay={0.55}>
        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6">
          <Field id="first_name" label={s.fields.first_name} value={value.first_name} onChange={set("first_name")} required testid="pi-first-name" autoComplete="given-name" />
          <Field id="last_name" label={s.fields.last_name} value={value.last_name} onChange={set("last_name")} required testid="pi-last-name" autoComplete="family-name" />
          <Field id="dob" label={s.fields.dob} value={value.dob} onChange={set("dob")} type="date" testid="pi-dob" />
          <Field id="nationality" label={s.fields.nationality} value={value.nationality} onChange={set("nationality")} testid="pi-nationality" />
          <Field id="country" label={s.fields.country} value={value.country} onChange={set("country")} testid="pi-country" required />
          <Field id="city" label={s.fields.city} value={value.city} onChange={set("city")} testid="pi-city" />
          <div className="md:col-span-2">
            <Field id="address" label={s.fields.address} value={value.address} onChange={set("address")} testid="pi-address" autoComplete="street-address" />
          </div>
          <Field id="postal_code" label={s.fields.postal_code} value={value.postal_code} onChange={set("postal_code")} testid="pi-postal-code" />
          <Field id="mobile" label={s.fields.mobile} value={value.mobile} onChange={set("mobile")} type="tel" testid="pi-mobile" autoComplete="tel" />
          <Field id="email" label={s.fields.email} value={value.email} onChange={set("email")} type="email" required testid="pi-email" autoComplete="email" />
          <Field id="prof_email" label={s.fields.prof_email} value={value.prof_email} onChange={set("prof_email")} type="email" testid="pi-prof-email" />
          <div className="space-y-2">
            <Label className="text-[10px] tracking-luxe uppercase text-white/50">
              {s.fields.language}
            </Label>
            <Select value={value.language || ""} onValueChange={set("language")}>
              <SelectTrigger className="luxe-input h-12 px-4" data-testid="pi-language">
                <SelectValue placeholder="—" />
              </SelectTrigger>
              <SelectContent className="bg-neutral-950 border-white/10 text-white">
                {s.languages.map((l) => (
                  <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Field id="linkedin" label={s.fields.linkedin} value={value.linkedin} onChange={set("linkedin")} testid="pi-linkedin" />
          <Field id="website" label={s.fields.website} value={value.website} onChange={set("website")} testid="pi-website" />
          <Field id="occupation" label={s.fields.occupation} value={value.occupation} onChange={set("occupation")} testid="pi-occupation" />
          <Field id="company" label={s.fields.company} value={value.company} onChange={set("company")} testid="pi-company" />
          <Field id="job_title" label={s.fields.job_title} value={value.job_title} onChange={set("job_title")} testid="pi-job-title" />
        </div>
      </FadeInUp>
    </div>
  );
};

// ============================================================
// Step: Profile Selection (multi-select cards)
// ============================================================
const ProfileSelectionStep = ({ tt, selected, onChange }) => {
  const s = tt.stages.selection;
  const toggle = (v) => {
    if (selected.includes(v)) onChange(selected.filter((x) => x !== v));
    else onChange([...selected, v]);
  };
  return (
    <div>
      <Chapter number={s.chapter} label={s.label} />
      <div className="mt-6">
        <RevealTitle className="text-3xl sm:text-4xl lg:text-5xl">{s.title}</RevealTitle>
      </div>
      <FadeInUp delay={0.3}>
        <p className="mt-5 text-white/60 max-w-2xl leading-relaxed">{s.subtitle}</p>
        <p className="mt-2 text-[10px] tracking-luxe uppercase text-[#e6c98a]/80">
          {s.multiple}
        </p>
      </FadeInUp>

      <FadeInUp delay={0.45}>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-4">
          {s.profiles.map((p, i) => {
            const active = selected.includes(p.value);
            return (
              <motion.button
                type="button"
                key={p.value}
                onClick={() => toggle(p.value)}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.05 * i, duration: 0.6 }}
                data-testid={`ps-profile-${p.value}`}
                className={`text-left p-6 border rounded transition-all duration-300 ${
                  active
                    ? "border-[#e6c98a] bg-[#e6c98a]/5"
                    : "border-white/10 hover:border-white/25 bg-white/[0.02]"
                }`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className={`font-serif-display text-lg md:text-xl ${active ? "text-[#e6c98a]" : "text-white"}`}>
                      {p.title}
                    </div>
                    <p className="mt-2 text-white/60 text-sm leading-relaxed">{p.desc}</p>
                  </div>
                  <div
                    className={`shrink-0 w-5 h-5 rounded border flex items-center justify-center mt-1 transition-colors ${
                      active
                        ? "border-[#e6c98a] bg-[#e6c98a] text-black"
                        : "border-white/25"
                    }`}
                  >
                    {active && <Check size={12} />}
                  </div>
                </div>
              </motion.button>
            );
          })}
        </div>
      </FadeInUp>
    </div>
  );
};

// ============================================================
// Step: Profile-specific questions (dynamic)
// ============================================================
const YesNoRow = ({ tt, id, label, value, onChange, options }) => {
  const opts = options || [tt.stages.questions.yes, tt.stages.questions.no];
  return (
    <div className="py-5 border-b border-white/5">
      <div className="text-white/85 text-sm md:text-base mb-3">{label}</div>
      <div className="flex flex-wrap gap-2">
        {opts.map((o) => (
          <button
            key={o}
            type="button"
            onClick={() => onChange(o)}
            data-testid={`${id}-${o}`}
            className={`px-4 py-2 rounded-full text-[10px] tracking-luxe uppercase border transition-colors ${
              value === o
                ? "border-[#e6c98a] bg-[#e6c98a]/10 text-[#e6c98a]"
                : "border-white/15 text-white/60 hover:border-white/35 hover:text-white"
            }`}
          >
            {o}
          </button>
        ))}
      </div>
    </div>
  );
};

const MultiTagRow = ({ id, label, options, values, onChange }) => (
  <div className="py-5 border-b border-white/5">
    <div className="text-white/85 text-sm md:text-base mb-3">{label}</div>
    <div className="flex flex-wrap gap-2">
      {options.map((o) => {
        const active = (values || []).includes(o);
        return (
          <button
            key={o}
            type="button"
            onClick={() => {
              const next = active ? values.filter((v) => v !== o) : [...(values || []), o];
              onChange(next);
            }}
            data-testid={`${id}-${o}`}
            className={`px-4 py-2 rounded-full text-[10px] tracking-luxe uppercase border transition-colors ${
              active
                ? "border-[#e6c98a] bg-[#e6c98a]/10 text-[#e6c98a]"
                : "border-white/15 text-white/60 hover:border-white/35 hover:text-white"
            }`}
          >
            {o}
          </button>
        );
      })}
    </div>
  </div>
);

const TextRow = ({ id, label, value, onChange, textarea = false, placeholder }) => (
  <div className="py-5 border-b border-white/5">
    <div className="text-white/85 text-sm md:text-base mb-3">{label}</div>
    {textarea ? (
      <Textarea
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="luxe-input p-4 min-h-[100px]"
        placeholder={placeholder}
        data-testid={id}
      />
    ) : (
      <Input
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="luxe-input h-11 px-4"
        placeholder={placeholder}
        data-testid={id}
      />
    )}
  </div>
);

const RANGES_FR = ["Moins de 25 000€", "25 000€ – 100 000€", "100 000€ – 500 000€", "500 000€ – 1M€", "1M€ – 5M€", "5M€ – 25M€", "25M€+"];
const RANGES_EN = ["Under €25,000", "€25,000 – €100,000", "€100,000 – €500,000", "€500,000 – €1M", "€1M – €5M", "€5M – €25M", "€25M+"];

const INTERESTS_FR = ["Automobile", "Immobilier", "Aviation", "Yachting", "Horlogerie & Joaillerie", "Investissements", "Opportunités d'affaires", "Ressources naturelles", "Sport", "Divertissement", "Accès exclusifs", "Autre"];
const INTERESTS_EN = ["Automotive", "Real Estate", "Aviation", "Yachting", "Watches & Jewellery", "Investment Opportunities", "Business Opportunities", "Natural Resources", "Sports", "Entertainment", "Exclusive Access", "Other"];

const INVESTOR_TYPES_FR = ["Individu", "Société", "Holding", "Family Office", "Fonds d'investissement", "Autre"];
const INVESTOR_TYPES_EN = ["Individual", "Company", "Holding Company", "Family Office", "Investment Fund", "Other"];

const INVEST_KINDS_FR = ["Equity", "Dette", "Private Equity", "Venture Capital", "Immobilier", "Joint Venture", "Acquisition", "Commodities", "Autre"];
const INVEST_KINDS_EN = ["Equity", "Debt", "Private Equity", "Venture Capital", "Real Estate", "Joint Venture", "Acquisition", "Commodities", "Other"];

const INTRODUCES_FR = ["Acheteur", "Vendeur", "Investisseur", "Propriétaire d'actifs", "Entreprise", "Professionnel", "Talent", "Autre"];
const INTRODUCES_EN = ["Buyer", "Seller", "Investor", "Asset Owner", "Company", "Professional", "Talent", "Other"];

const COMPENSATION_FR = ["Finder's Fee", "Referral Fee", "Commission", "Success Fee", "Au cas par cas", "Aucune"];
const COMPENSATION_EN = ["Finder's Fee", "Referral Fee", "Commission", "Success Fee", "Case-by-case", "No compensation"];

const ProfileQuestionsStep = ({ tt, locale, selectedProfiles, value, onChange }) => {
  const s = tt.stages.questions;
  const set = (path) => (v) => {
    const next = { ...value };
    const [p, k] = path.split(".");
    next[p] = { ...(next[p] || {}), [k]: v };
    onChange(next);
  };
  const g = (path) => {
    const [p, k] = path.split(".");
    return value?.[p]?.[k];
  };
  const isFr = locale === "fr";
  return (
    <div>
      <Chapter number={s.chapter} label={s.label} />
      <div className="mt-6">
        <RevealTitle className="text-3xl sm:text-4xl lg:text-5xl">{s.title}</RevealTitle>
      </div>
      <FadeInUp delay={0.3}>
        <p className="mt-5 text-white/60 max-w-2xl leading-relaxed">{s.subtitle}</p>
      </FadeInUp>

      {selectedProfiles.length === 0 && (
        <FadeInUp delay={0.5}>
          <div className="mt-14 border border-white/10 rounded p-8 text-white/50 text-sm">
            {isFr
              ? "Sélectionnez d'abord un ou plusieurs profils à l'étape précédente."
              : "Please first select one or more profiles in the previous step."}
          </div>
        </FadeInUp>
      )}

      <div className="mt-14 space-y-14">
        {selectedProfiles.includes("private_client") && (
          <ProfileBlock title={isFr ? "Client Privé" : "Private Client"}>
            <MultiTagRow
              id="pc-interests"
              label={isFr ? "Qu'est-ce qui vous intéresse ?" : "What are you interested in?"}
              options={isFr ? INTERESTS_FR : INTERESTS_EN}
              values={g("private_client.interests") || []}
              onChange={set("private_client.interests")}
            />
            <MultiTagRow
              id="pc-range"
              label={isFr ? "Fourchette de transaction habituelle" : "Typical transaction range"}
              options={isFr ? RANGES_FR : RANGES_EN}
              values={g("private_client.range") || []}
              onChange={set("private_client.range")}
            />
            <TextRow
              id="pc-countries"
              label={isFr ? "Pays ou régions ciblés" : "Countries or regions of interest"}
              value={g("private_client.countries")}
              onChange={set("private_client.countries")}
            />
            <YesNoRow
              tt={tt}
              id="pc-purpose"
              label={isFr ? "Vous nous rejoignez principalement pour :" : "You are joining primarily for:"}
              value={g("private_client.purpose")}
              onChange={set("private_client.purpose")}
              options={isFr ? ["Personnel", "Professionnel", "Les deux"] : ["Personal", "Business", "Both"]}
            />
            <TextRow
              id="pc-expectations"
              label={isFr ? "Attentes vis-à-vis de Somnium Group" : "What you expect from Somnium Group"}
              textarea
              value={g("private_client.expectations")}
              onChange={set("private_client.expectations")}
            />
          </ProfileBlock>
        )}

        {selectedProfiles.includes("buyer") && (
          <ProfileBlock title={isFr ? "Acheteur" : "Buyer"}>
            <TextRow id="b-target" label={isFr ? "Que cherchez-vous à acquérir ?" : "What are you looking to acquire?"} value={g("buyer.target")} onChange={set("buyer.target")} />
            <TextRow id="b-country" label={isFr ? "Pays / région" : "Country / region"} value={g("buyer.country")} onChange={set("buyer.country")} />
            <TextRow id="b-budget" label={isFr ? "Budget estimé" : "Estimated budget"} value={g("buyer.budget")} onChange={set("buyer.budget")} />
            <TextRow id="b-date" label={isFr ? "Date cible de transaction" : "Target transaction date"} value={g("buyer.date")} onChange={set("buyer.date")} />
            <YesNoRow tt={tt} id="b-final" label={isFr ? "Êtes-vous l'acheteur final ?" : "Are you the final buyer?"} value={g("buyer.final")} onChange={set("buyer.final")} options={[s.yes, s.no]} />
            {(g("buyer.final") === s.no) && (
              <>
                <TextRow id="b-representing" label={isFr ? "Qui représentez-vous ?" : "Who are you representing?"} value={g("buyer.representing")} onChange={set("buyer.representing")} />
                <YesNoRow tt={tt} id="b-authority" label={isFr ? "Êtes-vous autorisé à les représenter ?" : "Are you authorised to represent them?"} value={g("buyer.authority")} onChange={set("buyer.authority")} options={[s.yes, s.no]} />
              </>
            )}
            <YesNoRow tt={tt} id="b-funds" label={isFr ? "Les fonds sont-ils disponibles pour l'acquisition envisagée ?" : "Are funds currently available for the intended acquisition?"} value={g("buyer.funds")} onChange={set("buyer.funds")} options={[s.yes, s.no]} />
            <YesNoRow tt={tt} id="b-pof" label={isFr ? "Prêt à fournir une Preuve de Fonds lorsque nécessaire ?" : "Are you willing to provide Proof of Funds when required?"} value={g("buyer.pof")} onChange={set("buyer.pof")} options={[s.yes, s.no]} />
            <YesNoRow tt={tt} id="b-history" label={isFr ? "Avez-vous réalisé des acquisitions comparables auparavant ?" : "Have you previously completed comparable acquisitions?"} value={g("buyer.history")} onChange={set("buyer.history")} options={[s.yes, s.no]} />
          </ProfileBlock>
        )}

        {selectedProfiles.includes("investor") && (
          <ProfileBlock title={isFr ? "Investisseur" : "Investor"}>
            <MultiTagRow id="i-form" label={isFr ? "Vous investissez en tant que" : "You invest as"} options={isFr ? INVESTOR_TYPES_FR : INVESTOR_TYPES_EN} values={g("investor.form") || []} onChange={set("investor.form")} />
            <TextRow id="i-amount" label={isFr ? "Montant d'investissement typique" : "Typical investment amount"} value={g("investor.amount")} onChange={set("investor.amount")} />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <TextRow id="i-min" label={isFr ? "Minimum" : "Minimum"} value={g("investor.min")} onChange={set("investor.min")} />
              <TextRow id="i-max" label={isFr ? "Maximum" : "Maximum"} value={g("investor.max")} onChange={set("investor.max")} />
            </div>
            <TextRow id="i-sectors" label={isFr ? "Secteurs préférés" : "Preferred sectors"} value={g("investor.sectors")} onChange={set("investor.sectors")} />
            <TextRow id="i-countries" label={isFr ? "Pays préférés" : "Preferred countries"} value={g("investor.countries")} onChange={set("investor.countries")} />
            <MultiTagRow id="i-kind" label={isFr ? "Type d'investissement" : "Investment type"} options={isFr ? INVEST_KINDS_FR : INVEST_KINDS_EN} values={g("investor.kinds") || []} onChange={set("investor.kinds")} />
            <YesNoRow tt={tt} id="i-decision" label={isFr ? "Êtes-vous le décideur final ?" : "Are you the final decision-maker?"} value={g("investor.decision")} onChange={set("investor.decision")} options={[s.yes, s.no]} />
            {(g("investor.decision") === s.no) && (
              <TextRow id="i-process" label={isFr ? "Décrivez le processus d'approbation" : "Describe the approval process"} value={g("investor.process")} onChange={set("investor.process")} textarea />
            )}
            <YesNoRow tt={tt} id="i-pof" label={isFr ? "Prêt à fournir une preuve de capacité financière lorsque requis ?" : "Willing to provide Proof of Funds when required?"} value={g("investor.pof")} onChange={set("investor.pof")} options={[s.yes, s.no]} />
          </ProfileBlock>
        )}

        {selectedProfiles.includes("seller") && (
          <ProfileBlock title={isFr ? "Propriétaire / Vendeur" : "Asset Owner / Seller"}>
            <MultiTagRow id="se-categories" label={isFr ? "Types d'actifs ou d'opportunités" : "Asset or opportunity types"} options={isFr ? INTERESTS_FR : INTERESTS_EN} values={g("seller.categories") || []} onChange={set("seller.categories")} />
            <YesNoRow tt={tt} id="se-owner" label={isFr ? "Vous détenez légalement les actifs à soumettre ?" : "Do you legally own the assets you intend to submit?"} value={g("seller.owner")} onChange={set("seller.owner")} options={[s.yes, s.no, s.some]} />
            {(g("seller.owner") === s.no || g("seller.owner") === s.some) && (
              <YesNoRow tt={tt} id="se-auth" label={isFr ? "Êtes-vous légalement autorisé à représenter le propriétaire ?" : "Are you legally authorised to represent the owner?"} value={g("seller.auth")} onChange={set("seller.auth")} options={[s.yes, s.no]} />
            )}
            <TextRow id="se-value" label={isFr ? "Valeur typique des actifs" : "Typical asset value"} value={g("seller.value")} onChange={set("seller.value")} />
            <TextRow id="se-locations" label={isFr ? "Pays où se trouvent les actifs" : "Countries where assets are located"} value={g("seller.locations")} onChange={set("seller.locations")} />
            <YesNoRow tt={tt} id="se-others" label={isFr ? "Ces actifs sont-ils représentés par d'autres intermédiaires ?" : "Are these assets currently represented by other brokers?"} value={g("seller.others")} onChange={set("seller.others")} options={[s.yes, s.no]} />
            <YesNoRow tt={tt} id="se-exclusive" label={isFr ? "Détenez-vous des mandats exclusifs ?" : "Do you hold exclusive mandates?"} value={g("seller.exclusive")} onChange={set("seller.exclusive")} options={[s.yes, s.no, s.sometimes]} />
          </ProfileBlock>
        )}

        {selectedProfiles.includes("introducer") && (
          <ProfileBlock title={isFr ? "Apporteur d'affaires" : "Business Introducer"}>
            <MultiTagRow id="in-types" label={isFr ? "Quels types d'opportunités apportez-vous ?" : "What type of opportunities do you introduce?"} options={isFr ? INTRODUCES_FR : INTRODUCES_EN} values={g("introducer.types") || []} onChange={set("introducer.types")} />
            <TextRow id="in-industries" label={isFr ? "Industries" : "Industries"} value={g("introducer.industries")} onChange={set("introducer.industries")} />
            <TextRow id="in-countries" label={isFr ? "Pays" : "Countries"} value={g("introducer.countries")} onChange={set("introducer.countries")} />
            <YesNoRow tt={tt} id="in-know" label={isFr ? "Connaissez-vous personnellement les parties principales ?" : "Do you personally know the principal parties?"} value={g("introducer.know")} onChange={set("introducer.know")} options={[s.always, s.often, s.sometimes, s.rarely]} />
            <YesNoRow tt={tt} id="in-mandate" label={isFr ? "Travaillez-vous sous mandat écrit ?" : "Do you normally work under a written mandate?"} value={g("introducer.mandate")} onChange={set("introducer.mandate")} options={[s.always, s.sometimes, s.never]} />
            <YesNoRow tt={tt} id="in-chain" label={isFr ? "Combien d'intermédiaires entre vous et le principal ?" : "How many intermediaries between you and the principal?"} value={g("introducer.chain")} onChange={set("introducer.chain")} options={["0", "1", "2", "3+"]} />
            <YesNoRow tt={tt} id="in-disclose" label={isFr ? "Prêt à divulguer confidentiellement la chaîne complète ?" : "Prepared to confidentially disclose the complete intermediary chain?"} value={g("introducer.disclose")} onChange={set("introducer.disclose")} options={[s.yes, s.no]} />
            <YesNoRow tt={tt} id="in-verify" label={isFr ? "Prêt à permettre la vérification directe avec le principal ?" : "Prepared to allow direct verification with the principal?"} value={g("introducer.verify")} onChange={set("introducer.verify")} options={[s.yes, s.no]} />
            <MultiTagRow id="in-compensation" label={isFr ? "Rémunération attendue" : "Expected compensation"} options={isFr ? COMPENSATION_FR : COMPENSATION_EN} values={g("introducer.compensation") || []} onChange={set("introducer.compensation")} />
          </ProfileBlock>
        )}

        {selectedProfiles.includes("professional") && (
          <ProfileBlock title={isFr ? "Professionnel / Partenaire" : "Professional / Partner"}>
            <TextRow id="pr-profession" label={isFr ? "Profession" : "Profession"} value={g("professional.profession")} onChange={set("professional.profession")} />
            <TextRow id="pr-company" label={isFr ? "Entreprise" : "Company"} value={g("professional.company")} onChange={set("professional.company")} />
            <TextRow id="pr-title" label={isFr ? "Fonction" : "Job title"} value={g("professional.title")} onChange={set("professional.title")} />
            <TextRow id="pr-industry" label={isFr ? "Industrie" : "Industry"} value={g("professional.industry")} onChange={set("professional.industry")} />
            <TextRow id="pr-country" label={isFr ? "Pays" : "Country"} value={g("professional.country")} onChange={set("professional.country")} />
            <TextRow id="pr-reg" label={isFr ? "Numéro d'enregistrement de l'entreprise" : "Company registration number"} value={g("professional.reg")} onChange={set("professional.reg")} />
            <TextRow id="pr-website" label={isFr ? "Site web" : "Website"} value={g("professional.website")} onChange={set("professional.website")} />
            <TextRow id="pr-years" label={isFr ? "Années d'activité" : "Years in business"} value={g("professional.years")} onChange={set("professional.years")} />
            <YesNoRow tt={tt} id="pr-regulated" label={isFr ? "Opérez-vous dans une profession réglementée ?" : "Regulated profession?"} value={g("professional.regulated")} onChange={set("professional.regulated")} options={[s.yes, s.no]} />
            {(g("professional.regulated") === s.yes) && (
              <>
                <TextRow id="pr-authority" label={isFr ? "Autorité de régulation" : "Regulatory authority"} value={g("professional.authority")} onChange={set("professional.authority")} />
                <TextRow id="pr-licence" label={isFr ? "Numéro de licence" : "Licence number"} value={g("professional.licence")} onChange={set("professional.licence")} />
              </>
            )}
            <YesNoRow tt={tt} id="pr-liability" label={isFr ? "Assurance responsabilité professionnelle ?" : "Professional liability insurance?"} value={g("professional.liability")} onChange={set("professional.liability")} options={[s.yes, s.no]} />
            <TextRow id="pr-services" label={isFr ? "Services que vous pouvez fournir" : "Services you can provide"} value={g("professional.services")} onChange={set("professional.services")} textarea />
            <TextRow id="pr-markets" label={isFr ? "Marchés géographiques couverts" : "Geographic markets covered"} value={g("professional.markets")} onChange={set("professional.markets")} />
          </ProfileBlock>
        )}

        {selectedProfiles.includes("company") && (
          <ProfileBlock title={isFr ? "Représentant d'entreprise" : "Company Representative"}>
            <TextRow id="c-legal" label={isFr ? "Raison sociale" : "Legal company name"} value={g("company.legal")} onChange={set("company.legal")} />
            <TextRow id="c-trading" label={isFr ? "Nom commercial" : "Trading name"} value={g("company.trading")} onChange={set("company.trading")} />
            <TextRow id="c-country" label={isFr ? "Pays d'incorporation" : "Country of incorporation"} value={g("company.country")} onChange={set("company.country")} />
            <TextRow id="c-reg" label={isFr ? "Numéro d'enregistrement" : "Registration number"} value={g("company.reg")} onChange={set("company.reg")} />
            <TextRow id="c-office" label={isFr ? "Siège social" : "Registered office"} value={g("company.office")} onChange={set("company.office")} />
            <TextRow id="c-website" label={isFr ? "Site web" : "Website"} value={g("company.website")} onChange={set("company.website")} />
            <TextRow id="c-inc-date" label={isFr ? "Date d'incorporation" : "Date of incorporation"} value={g("company.inc_date")} onChange={set("company.inc_date")} />
            <TextRow id="c-activity" label={isFr ? "Activité principale" : "Main activity"} value={g("company.activity")} onChange={set("company.activity")} />
            <TextRow id="c-position" label={isFr ? "Fonction dans l'entreprise" : "Position in the company"} value={g("company.position")} onChange={set("company.position")} />
            <YesNoRow tt={tt} id="c-auth" label={isFr ? "Êtes-vous autorisé à représenter l'entreprise ?" : "Are you authorised to represent the company?"} value={g("company.auth")} onChange={set("company.auth")} options={[s.yes, s.no]} />
          </ProfileBlock>
        )}
      </div>
    </div>
  );
};

const ProfileBlock = ({ title, children }) => (
  <motion.div
    initial={{ opacity: 0, y: 16 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
    className="border-l-2 border-[#e6c98a]/40 pl-6 md:pl-10"
  >
    <h3 className="font-serif-display text-white text-2xl md:text-3xl mb-4">
      {title}
    </h3>
    {children}
  </motion.div>
);

// ============================================================
// Step: Declarations
// ============================================================
const DeclarationsStep = ({ tt, value, onChange }) => {
  const s = tt.stages.declarations;
  const checked = value.checked || [];
  const toggle = (i) => {
    const next = checked.includes(i) ? checked.filter((x) => x !== i) : [...checked, i];
    onChange({ ...value, checked: next });
  };
  return (
    <div>
      <Chapter number={s.chapter} label={s.label} />
      <div className="mt-6">
        <RevealTitle className="text-3xl sm:text-4xl lg:text-5xl">{s.title}</RevealTitle>
      </div>
      <FadeInUp delay={0.3}>
        <p className="mt-5 text-white/60 max-w-2xl leading-relaxed">{s.subtitle}</p>
      </FadeInUp>

      <FadeInUp delay={0.45}>
        <div className="mt-12 space-y-4">
          {s.items.map((item, i) => (
            <label
              key={i}
              className={`flex items-start gap-4 p-5 border rounded cursor-pointer transition-colors ${
                checked.includes(i)
                  ? "border-[#e6c98a]/60 bg-[#e6c98a]/5"
                  : "border-white/10 hover:border-white/25 bg-white/[0.02]"
              }`}
            >
              <Checkbox
                checked={checked.includes(i)}
                onCheckedChange={() => toggle(i)}
                className="mt-1 border-white/30 data-[state=checked]:bg-[#e6c98a] data-[state=checked]:border-[#e6c98a] data-[state=checked]:text-black"
                data-testid={`decl-check-${i}`}
              />
              <span className="text-white/85 text-sm md:text-base leading-relaxed">{item}</span>
            </label>
          ))}
        </div>
      </FadeInUp>

      <FadeInUp delay={0.6}>
        <div className="mt-14 border border-white/10 rounded p-6 md:p-8">
          <div className="text-[10px] tracking-luxe uppercase text-white/50 mb-6">
            {s.signatureTitle}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Field
              id="sig-name"
              label={s.signatureName}
              value={value.signature_name}
              onChange={(v) => onChange({ ...value, signature_name: v })}
              testid="decl-sig-name"
              required
            />
            <Field
              id="sig-date"
              label={s.signatureDate}
              value={value.signature_date || new Date().toISOString().slice(0, 10)}
              onChange={(v) => onChange({ ...value, signature_date: v })}
              type="date"
              testid="decl-sig-date"
            />
          </div>
          <p className="mt-6 text-white/45 text-xs">{s.signatureAck}</p>
        </div>
      </FadeInUp>
    </div>
  );
};

// ============================================================
// Step: Review
// ============================================================
const ReviewStep = ({ tt, personal, selectedProfiles, profileAnswers, declarations, onEdit, allCompleteMap }) => {
  const s = tt.stages.review;
  const rows = [
    { key: "personal", label: s.personal, complete: allCompleteMap.personal, stepKey: "personal" },
    { key: "selection", label: s.selection, complete: allCompleteMap.selection, stepKey: "selection" },
    { key: "questions", label: s.questions, complete: allCompleteMap.questions, stepKey: "questions" },
    { key: "declarations", label: s.declarations, complete: allCompleteMap.declarations, stepKey: "declarations" },
  ];
  return (
    <div>
      <Chapter number={s.chapter} label={s.label} />
      <div className="mt-6">
        <RevealTitle className="text-3xl sm:text-4xl lg:text-5xl">{s.title}</RevealTitle>
      </div>
      <FadeInUp delay={0.3}>
        <p className="mt-5 text-white/60 max-w-2xl leading-relaxed">{s.subtitle}</p>
      </FadeInUp>
      <FadeInUp delay={0.45}>
        <div className="mt-12 space-y-3">
          {rows.map((r) => (
            <div key={r.key} className="flex items-center justify-between border border-white/10 rounded p-5 bg-white/[0.02]">
              <div className="flex items-center gap-4">
                <span
                  className={`inline-flex w-6 h-6 items-center justify-center rounded-full border ${
                    r.complete
                      ? "border-[#e6c98a] text-[#e6c98a]"
                      : "border-amber-300/40 text-amber-300/70"
                  }`}
                >
                  {r.complete ? <Check size={12} /> : "!"}
                </span>
                <div>
                  <div className="text-white/85 text-sm md:text-base">{r.label}</div>
                  <div className={`text-[10px] tracking-luxe uppercase mt-1 ${r.complete ? "text-[#e6c98a]/80" : "text-amber-300/80"}`}>
                    {r.complete ? s.complete : s.incomplete}
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={() => onEdit(r.stepKey)}
                className="text-[10px] tracking-luxe uppercase text-white/50 hover:text-white transition-colors"
                data-testid={`review-edit-${r.key}`}
              >
                {s.edit}
              </button>
            </div>
          ))}
        </div>
        {!rows.every((r) => r.complete) && (
          <div className="mt-6 border border-amber-300/30 bg-amber-300/5 rounded p-4 text-amber-200/80 text-sm">
            {s.missingWarning}
          </div>
        )}
      </FadeInUp>
    </div>
  );
};

// ============================================================
// Submitted (post-submit)
// ============================================================
const SubmittedScreen = ({ t, tt, application, locale, onLangToggle }) => (
  <ScreenShell>
    <AppHeader
      locale={locale}
      onLangToggle={onLangToggle}
      langLabel={locale === "fr" ? "EN" : "FR"}
      refNumber={application.reference_number}
      progress={100}
    />
    <main className="max-w-3xl mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-32">
      <FadeInUp>
        <Chapter number="—" label={tt.eyebrow} />
      </FadeInUp>
      <div className="mt-8">
        <RevealTitle className="text-4xl sm:text-5xl lg:text-6xl">{tt.title}</RevealTitle>
      </div>
      <FadeInUp delay={0.35}>
        <p className="mt-8 text-white/70 text-base md:text-lg leading-relaxed max-w-2xl">
          {tt.intro}
        </p>
      </FadeInUp>

      <FadeInUp delay={0.55}>
        <div className="mt-14 border border-white/10 rounded p-6 md:p-8 max-w-lg">
          <div className="text-[10px] tracking-luxe uppercase text-white/40 mb-2">
            Reference
          </div>
          <div className="font-serif-display text-[#e6c98a] text-3xl tracking-mid">
            {application.reference_number}
          </div>
        </div>
      </FadeInUp>

      <FadeInUp delay={0.75}>
        <ol className="mt-16 space-y-3">
          {tt.timeline.map((t, i) => {
            const state = t.done === true ? "done" : t.done === "current" ? "current" : "pending";
            return (
              <li
                key={t.key}
                className="flex items-center justify-between border-b border-white/5 py-3"
              >
                <div className="flex items-center gap-4">
                  <span
                    className={`inline-flex w-5 h-5 items-center justify-center rounded-full border text-[10px] ${
                      state === "done"
                        ? "border-[#e6c98a] bg-[#e6c98a] text-black"
                        : state === "current"
                        ? "border-white text-white animate-pulse"
                        : "border-white/15 text-white/30"
                    }`}
                  >
                    {state === "done" ? <Check size={10} /> : i + 1}
                  </span>
                  <span
                    className={`text-sm md:text-base ${
                      state === "pending" ? "text-white/40" : "text-white/85"
                    }`}
                  >
                    {t.label}
                  </span>
                </div>
                {t.note && <span className="text-[10px] tracking-luxe uppercase text-white/30">{t.note}</span>}
              </li>
            );
          })}
        </ol>
      </FadeInUp>

      <FadeInUp delay={0.95}>
        <div className="mt-14 text-white/40 text-xs tracking-luxe uppercase">
          {tt.close}
        </div>
      </FadeInUp>
    </main>
  </ScreenShell>
);

// ============================================================
// Main wizard container
// ============================================================
const isPersonalComplete = (p = {}) =>
  Boolean(p.first_name && p.last_name && p.country && p.email);
const isSelectionComplete = (sel = []) => sel.length > 0;
const isQuestionsComplete = (sel = [], ans = {}) =>
  sel.length === 0 ? false : sel.every((s) => ans && ans[s] && Object.keys(ans[s]).length > 0);
const isDeclarationsComplete = (d = {}) =>
  Array.isArray(d.checked) && d.checked.length >= 8 && Boolean(d.signature_name);
const isIdentityComplete = (docs = []) => {
  const kinds = new Set((docs || []).map((d) => d.kind));
  return kinds.has("selfie") && kinds.has("id_front");
};

const Wizard = ({ t, tt, application, setApplication, locale, onLangToggle }) => {
  const w = tt.wizard;
  const navigate = useNavigate();
  const [step, setStep] = useState("personal");
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [kycDocs, setKycDocs] = useState([]);
  const shellRef = useRef(null);

  // Poll KYC docs when the wizard mounts + whenever the step changes to identity
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/application/${application.token}/documents`);
        if (alive) setKycDocs(data.items || []);
      } catch { /* ignore */ }
    })();
    return () => { alive = false; };
  }, [application.token, step]);

  const personal = useMemo(() => application.personal_info || {}, [application.personal_info]);
  const selectedProfiles = useMemo(() => application.selected_profiles || [], [application.selected_profiles]);
  const profileAnswers = useMemo(() => application.profile_answers || {}, [application.profile_answers]);
  const declarations = useMemo(() => application.declarations || {}, [application.declarations]);

  const completeMap = useMemo(
    () => ({
      personal: isPersonalComplete(personal),
      selection: isSelectionComplete(selectedProfiles),
      questions: isQuestionsComplete(selectedProfiles, profileAnswers),
      declarations: isDeclarationsComplete(declarations),
      identity: isIdentityComplete(kycDocs),
    }),
    [personal, selectedProfiles, profileAnswers, declarations, kycDocs]
  );

  const progress = useMemo(() => {
    const total = 5;
    const done = Object.values(completeMap).filter(Boolean).length;
    return Math.min(100, Math.round((done / total) * 100));
  }, [completeMap]);

  const patch = useCallback(async (updates) => {
    setSaving(true);
    try {
      const currentStepNumber = STEP_KEY_TO_NUMBER[step] || 0;
      const { data } = await axios.patch(
        `${API}/application/${application.token}`,
        { ...updates, progress, current_step: currentStepNumber }
      );
      setApplication(data);
    } catch (err) {
      // ignore
    } finally {
      setSaving(false);
    }
  }, [application.token, progress, setApplication, step]);

  // Autosave on step change or field change (debounced via effect)
  useEffect(() => {
    const t = setTimeout(() => {
      patch({
        personal_info: personal,
        selected_profiles: selectedProfiles,
        profile_answers: profileAnswers,
        declarations,
      });
    }, 900);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [personal, selectedProfiles, profileAnswers, declarations, step]);

  const goto = (key) => {
    setStep(key);
    // Scroll to top of the wizard content smoothly
    setTimeout(() => {
      shellRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  };

  const stepIdx = STEP_KEYS.indexOf(step);
  const canContinue = (() => {
    if (step === "personal") return completeMap.personal;
    if (step === "selection") return completeMap.selection;
    if (step === "questions") return completeMap.questions;
    if (step === "declarations") return completeMap.declarations;
    if (step === "identity") return completeMap.identity;
    return true;
  })();

  const doSubmit = async () => {
    setSubmitting(true);
    try {
      await patch({
        personal_info: personal,
        selected_profiles: selectedProfiles,
        profile_answers: profileAnswers,
        declarations,
      });
      const { data } = await axios.post(`${API}/application/${application.token}/submit`);
      setApplication(data);
      toast.success(locale === "fr" ? "Candidature soumise avec succès." : "Application submitted.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || w.stages.review.missingWarning);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ScreenShell>
      <AppHeader
        locale={locale}
        onLangToggle={onLangToggle}
        langLabel={locale === "fr" ? "EN" : "FR"}
        refNumber={application.reference_number}
        progress={progress}
        onExit={() => navigate("/")}
        exitLabel={w.exit}
      />
      <main ref={shellRef} className="max-w-4xl mx-auto px-6 md:px-10 pt-14 md:pt-20 pb-32">
        <WizardStepper tt={w} current={step} />

        <div className="hidden md:flex items-center justify-between text-[10px] tracking-luxe uppercase text-white/40 mb-8">
          <div>{w.header} · {application.reference_number}</div>
          <div>{progress}% {w.percentCompleted}</div>
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          >
            {step === "personal" && (
              <PersonalInfoStep
                tt={w}
                value={personal}
                onChange={(v) => setApplication({ ...application, personal_info: v })}
                contactLocale={application.locale || "fr"}
                onContactLocaleChange={(v) => {
                  setApplication({ ...application, locale: v });
                  // Persist immediately so upcoming emails use the new locale
                  patch({ locale: v });
                }}
              />
            )}
            {step === "selection" && (
              <ProfileSelectionStep
                tt={w}
                selected={selectedProfiles}
                onChange={(v) => setApplication({ ...application, selected_profiles: v })}
              />
            )}
            {step === "questions" && (
              <ProfileQuestionsStep
                tt={w}
                locale={locale}
                selectedProfiles={selectedProfiles}
                value={profileAnswers}
                onChange={(v) => setApplication({ ...application, profile_answers: v })}
              />
            )}
            {step === "declarations" && (
              <DeclarationsStep
                tt={w}
                value={declarations}
                onChange={(v) => setApplication({ ...application, declarations: v })}
              />
            )}
            {step === "identity" && (
              <IdentityVerificationStep
                token={application.token}
                locale={locale}
              />
            )}
            {step === "review" && (
              <ReviewStep
                tt={w}
                personal={personal}
                selectedProfiles={selectedProfiles}
                profileAnswers={profileAnswers}
                declarations={declarations}
                onEdit={(k) => goto(k)}
                allCompleteMap={completeMap}
              />
            )}
          </motion.div>
        </AnimatePresence>

        <div className="mt-16 flex flex-col-reverse md:flex-row items-stretch md:items-center justify-between gap-4 border-t border-white/5 pt-8">
          <button
            type="button"
            onClick={() => stepIdx > 0 && goto(STEP_KEYS[stepIdx - 1])}
            disabled={stepIdx === 0}
            className={`${luxeButtonGhost} ${stepIdx === 0 ? "opacity-30 cursor-not-allowed" : ""}`}
            data-testid="wiz-back"
          >
            <ArrowLeft size={14} /> {w.back}
          </button>
          <div className="flex items-center gap-3 text-[10px] tracking-luxe uppercase text-white/30">
            {saving ? <><Loader2 size={12} className="animate-spin" /> {locale === "fr" ? "Sauvegarde…" : "Saving…"}</> : <><ShieldCheck size={12} /> {locale === "fr" ? "Sauvegarde automatique" : "Auto-saved"}</>}
          </div>
          {step !== "review" ? (
            <button
              type="button"
              onClick={() => canContinue && goto(STEP_KEYS[stepIdx + 1])}
              disabled={!canContinue}
              className={`${luxeButtonPrimary} ${!canContinue ? "opacity-40 cursor-not-allowed" : ""}`}
              data-testid="wiz-next"
            >
              {w.continue} <ArrowRight size={14} />
            </button>
          ) : (
            <button
              type="button"
              onClick={doSubmit}
              disabled={submitting || !Object.values(completeMap).every(Boolean)}
              className={`${luxeButtonPrimary} ${(!Object.values(completeMap).every(Boolean) || submitting) ? "opacity-50 cursor-not-allowed" : ""}`}
              data-testid="wiz-submit"
            >
              {submitting ? <><Loader2 size={14} className="animate-spin" /> {w.submitting}</> : <>{w.submit} <ArrowRight size={14} /></>}
            </button>
          )}
        </div>
      </main>
    </ScreenShell>
  );
};

// ============================================================
// Page entry
// ============================================================
export default function ApplicationPage() {
  useLenis();
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const [locale, setLocale] = useState(searchParams.get("locale") === "en" ? "en" : "fr");
  const [application, setApplication] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [showConfirmation, setShowConfirmation] = useState(searchParams.get("fresh") === "1");

  const t = translations[locale];
  const tt = t.app;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/application/${token}`);
      setApplication(data);
      if (data?.locale && data.locale !== locale && !searchParams.get("locale")) {
        setLocale(data.locale);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || "Application not found";
      setError(detail);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    document.title = locale === "fr" ? "Somnium Group — Candidature" : "Somnium Group — Application";
    document.documentElement.lang = locale;
  }, [locale]);

  if (loading) {
    return (
      <ScreenShell>
        <div className="min-h-[100svh] flex items-center justify-center">
          <Loader2 className="animate-spin text-white/40" />
        </div>
      </ScreenShell>
    );
  }

  if (error || !application) {
    return (
      <ScreenShell>
        <div className="min-h-[100svh] flex flex-col items-center justify-center px-6 text-center">
          <img src={LOGO_URL} alt="Somnium Group" className="w-16 h-16 object-contain mb-8" draggable={false} />
          <h1 className="font-serif-display text-white text-3xl md:text-4xl mb-4">
            {locale === "fr" ? "Lien invalide ou expiré." : "Invalid or expired link."}
          </h1>
          <p className="text-white/60 max-w-md">
            {locale === "fr"
              ? "Ce lien de candidature n'est plus valide. Veuillez recommencer une demande depuis la page d'accueil."
              : "This application link is no longer valid. Please start a new request from the home page."}
          </p>
          <a href="/" className={`mt-10 ${luxeButtonGhost}`}>
            {locale === "fr" ? "Retour à l'accueil" : "Back to home"}
          </a>
        </div>
      </ScreenShell>
    );
  }

  const toggleLocale = () => setLocale(locale === "fr" ? "en" : "fr");

  // These statuses lock the wizard behind a submitted/final screen.
  // `info_requested` is intentionally NOT included: the candidate needs to
  // reopen the wizard to provide the missing element (the PATCH endpoint
  // flips status back to in_progress on first save).
  const finalOrSubmittedStatuses = new Set([
    "submitted",
    "documents_submitted",
    "in_review",
    "approved",
    "accepted",
    "rejected",
    "abandoned",
    "expired",
  ]);
  if (finalOrSubmittedStatuses.has(application.status)) {
    return <SubmittedScreen t={t} tt={tt.wizard.stages.submitted} application={application} locale={locale} onLangToggle={toggleLocale} />;
  }

  if (showConfirmation) {
    return (
      <ConfirmationScreen
        t={t}
        tt={tt.confirmation}
        application={application}
        locale={locale}
        onLangToggle={toggleLocale}
        onContinue={() => setShowConfirmation(false)}
        onLater={() => { window.location.href = "/"; }}
      />
    );
  }

  return (
    <Wizard
      t={t}
      tt={tt}
      application={application}
      setApplication={setApplication}
      locale={locale}
      onLangToggle={toggleLocale}
    />
  );
}
